import React, { createContext, useContext, useState } from 'react';
import { uploadVideoApi } from '../api/videos';

const VideoUploadContext = createContext();

export const useVideoUpload = () => {
  const context = useContext(VideoUploadContext);
  if (!context) {
    throw new Error('useVideoUpload must be used within a VideoUploadProvider');
  }
  return context;
};

export const VideoUploadProvider = ({ children }) => {
  const [uploadState, setUploadState] = useState({
    isVisible: false,
    isUploading: false,
    progress: 0,
    fileName: '',
    status: 'idle', // 'idle' | 'uploading' | 'processing' | 'success' | 'error'
    error: null,
    isMinimized: false,
  });

  const startUpload = async ({ file, title, description, tags, onSuccess }) => {
    if (!file || !title) return;

    setUploadState({
      isVisible: true,
      isUploading: true,
      progress: 0,
      fileName: file.name,
      status: 'uploading',
      error: null,
      isMinimized: false,
    });

    try {
      const formData = new FormData();
      formData.append('video', file);
      formData.append('title', title.trim());
      if (description?.trim()) formData.append('description', description.trim());
      if (tags?.trim()) formData.append('tags', tags.trim());

      const response = await uploadVideoApi(formData, (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadState((prev) => ({
            ...prev,
            progress: percentCompleted,
            status: percentCompleted >= 100 ? 'processing' : 'uploading',
          }));
        }
      });

      setUploadState((prev) => ({
        ...prev,
        isUploading: false,
        progress: 100,
        status: 'success',
      }));

      if (onSuccess) {
        onSuccess(response?.data || response);
      }

      // Auto-dismiss notification after 6 seconds of success
      setTimeout(() => {
        setUploadState((prev) => {
          if (prev.status === 'success') {
            return { ...prev, isVisible: false, status: 'idle' };
          }
          return prev;
        });
      }, 6000);

    } catch (err) {
      console.error('Background upload error:', err);
      const message = err.response?.data?.message || err.message || 'Video upload failed';
      setUploadState((prev) => ({
        ...prev,
        isUploading: false,
        status: 'error',
        error: message,
      }));
    }
  };

  const minimizeWidget = () => {
    setUploadState((prev) => ({ ...prev, isMinimized: true }));
  };

  const expandWidget = () => {
    setUploadState((prev) => ({ ...prev, isMinimized: false }));
  };

  const dismissWidget = () => {
    setUploadState((prev) => ({ ...prev, isVisible: false, status: 'idle' }));
  };

  return (
    <VideoUploadContext.Provider
      value={{
        uploadState,
        startUpload,
        minimizeWidget,
        expandWidget,
        dismissWidget,
      }}
    >
      {children}
    </VideoUploadContext.Provider>
  );
};
